// "Exhausted until 15:00" / "Sign in again" on a connection whose usage limit
// was hit (checkpoints contract §3.4): no new runs start on it until then.
// With `connectionId`, a "Clear" button marks it usable again
// (POST /executor-connections/:id/exhaustion/clear).

import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { ConnectionExhaustion } from '@goblic/shared'
import { connectionsApi } from '../lib/api-client'
import { exhaustionBadge } from '../lib/run-recovery'
import { StatusBadge } from './status-badge'
import { useDialogs } from './dialogs'
import { useWorkspaceId } from '../lib/auth'

export function ExhaustionBadge({
  exhaustion,
  connectionId,
}: {
  exhaustion: Partial<ConnectionExhaustion> | null | undefined
  /** Offer "Clear" for this connection. */
  connectionId?: string
}) {
  const badge = exhaustionBadge(exhaustion)
  const workspaceId = useWorkspaceId()
  const queryClient = useQueryClient()
  const { alert } = useDialogs()
  const clear = useMutation({
    mutationFn: (id: string) => connectionsApi.clearExhaustion(id, workspaceId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connections', workspaceId] })
      void queryClient.invalidateQueries({ queryKey: ['agents', workspaceId] })
    },
    onError: (error) =>
      void alert({
        kind: 'error',
        title: 'Couldn’t clear the limit',
        message: error instanceof Error ? error.message : undefined,
      }),
  })
  if (!badge) return null
  return (
    <span className="inline-flex flex-wrap items-center gap-2" title={badge.title}>
      <StatusBadge status={badge.tone === 'danger' ? 'error' : 'degraded'} label={badge.label} />
      {connectionId && badge.reason !== 'auth' ? (
        <button
          type="button"
          className="btn-ghost btn-sm"
          disabled={clear.isPending}
          onClick={(event) => {
            event.stopPropagation()
            clear.mutate(connectionId)
          }}
          title="Mark the connection usable again before the limit resets"
        >
          {clear.isPending ? 'Clearing…' : 'Clear'}
        </button>
      ) : null}
    </span>
  )
}
